import { isCustomerOwnedSessionStorageQuarantined } from "@/app/booking/core/customerOwnedSessionStorageBarrier";

import {
  validateAppointmentWaitlistSubmissionAttempt,
  type AppointmentWaitlistSubmissionAttemptV1,
} from "./appointmentWaitlistSubmissionAttempt";
import { migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage } from "./retiredGroupAppointmentWaitlistStorageMigration";
import { APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_STORAGE_MAX_SERIALIZED_CHARACTERS } from "./appointmentWaitlistStorageLimits";

export const APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_STORAGE_KEY =
  "volera:appointment-waitlist-submission-attempts:v1";

const STORE_VERSION = 1 as const;
const STORE_KIND = "appointment-waitlist-submission-attempts";
const MAX_ENTRIES = 8;
const MAX_AGE_MS = 24 * 60 * 60 * 1_000;
const MAX_SERIALIZED_CHARACTERS =
  APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_STORAGE_MAX_SERIALIZED_CHARACTERS;

interface StoredAttemptEntry {
  draftId: string;
  savedAtEpochMs: number;
  attempt: AppointmentWaitlistSubmissionAttemptV1;
}

interface StoredAttemptEnvelope {
  storeVersion: typeof STORE_VERSION;
  storeKind: typeof STORE_KIND;
  entries: readonly StoredAttemptEntry[];
}

/** Opaque, invocation-scoped authority to settle one already persisted row. */
export interface AppointmentWaitlistSubmissionAttemptReconciliationCapability {
  readonly kind: "appointment-waitlist-submission-attempt-reconciliation-capability";
}

interface CapabilityRecord {
  storage: Storage;
  draftId: string;
  customerAccountId: string;
  idempotencyKey: string;
  immutableFingerprint: string;
}

const capabilities = new WeakMap<
  AppointmentWaitlistSubmissionAttemptReconciliationCapability,
  CapabilityRecord
>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function immutableAttemptFingerprint(
  attempt: AppointmentWaitlistSubmissionAttemptV1,
) {
  const { state: omittedState, ...immutable } = attempt;
  void omittedState;
  return JSON.stringify(immutable);
}

function getStorage(evaluatedAtEpochMs?: number) {
  if (typeof window === "undefined") return null;
  try {
    if (
      !migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage(
        evaluatedAtEpochMs,
      )
    ) {
      return null;
    }
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function safeSubmissionAttemptEpoch(value?: number) {
  try {
    const epoch = value ?? Date.now();
    return Number.isSafeInteger(epoch) &&
      epoch >= 0 &&
      epoch <= 8_640_000_000_000_000
      ? epoch
      : null;
  } catch {
    return null;
  }
}

function normalizeEnvelope(
  value: unknown,
  now: number,
): StoredAttemptEntry[] | null {
  if (
    !isRecord(value) ||
    value.storeVersion !== STORE_VERSION ||
    value.storeKind !== STORE_KIND ||
    !Array.isArray(value.entries) ||
    value.entries.length > MAX_ENTRIES
  ) {
    return null;
  }
  const seen = new Set<string>();
  const entries: StoredAttemptEntry[] = [];
  for (const candidate of value.entries) {
    if (
      !isRecord(candidate) ||
      typeof candidate.draftId !== "string" ||
      candidate.draftId.length === 0 ||
      seen.has(candidate.draftId) ||
      typeof candidate.savedAtEpochMs !== "number" ||
      !Number.isSafeInteger(candidate.savedAtEpochMs) ||
      candidate.savedAtEpochMs < 0 ||
      candidate.savedAtEpochMs > now + 5 * 60_000
    ) {
      return null;
    }
    const validation = validateAppointmentWaitlistSubmissionAttempt(
      candidate.attempt,
      { evaluatedAtEpochMs: now },
    );
    if (!validation.valid || validation.attempt.draftId !== candidate.draftId)
      return null;
    seen.add(candidate.draftId);
    if (
      now - candidate.savedAtEpochMs > MAX_AGE_MS &&
      validation.attempt.state.kind === "not-applied"
    ) {
      continue;
    }
    entries.push({
      draftId: candidate.draftId,
      savedAtEpochMs: candidate.savedAtEpochMs,
      attempt: validation.attempt,
    });
  }
  return entries.sort(
    (left, right) => right.savedAtEpochMs - left.savedAtEpochMs,
  );
}

function readEntries(storage: Storage, evaluatedAtEpochMs?: number) {
  try {
    const now = evaluatedAtEpochMs ?? Date.now();
    if (!Number.isSafeInteger(now) || now < 0) return null;
    const raw = storage.getItem(
      APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_STORAGE_KEY,
    );
    if (!raw) return [] as StoredAttemptEntry[];
    if (raw.length > MAX_SERIALIZED_CHARACTERS) return null;
    return normalizeEnvelope(JSON.parse(raw) as unknown, now);
  } catch {
    return null;
  }
}

function persistEntries(
  storage: Storage,
  entries: readonly StoredAttemptEntry[],
  evaluatedAtEpochMs?: number,
) {
  const evaluatedEpoch = safeSubmissionAttemptEpoch(evaluatedAtEpochMs);
  if (evaluatedEpoch === null) return false;
  const serialized = JSON.stringify({
    storeVersion: STORE_VERSION,
    storeKind: STORE_KIND,
    entries,
  } satisfies StoredAttemptEnvelope);
  if (serialized.length > MAX_SERIALIZED_CHARACTERS) return false;
  try {
    if (entries.length === 0) {
      storage.removeItem(APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_STORAGE_KEY);
    } else {
      storage.setItem(
        APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_STORAGE_KEY,
        serialized,
      );
    }
    const confirmed = readEntries(storage, evaluatedEpoch);
    return (
      confirmed !== null &&
      JSON.stringify(confirmed) === JSON.stringify(entries)
    );
  } catch {
    return false;
  }
}

function haveSameImmutableAttempt(
  left: AppointmentWaitlistSubmissionAttemptV1,
  right: AppointmentWaitlistSubmissionAttemptV1,
) {
  return immutableAttemptFingerprint(left) === immutableAttemptFingerprint(right);
}

function isLegalStateTransition(
  current: AppointmentWaitlistSubmissionAttemptV1,
  next: AppointmentWaitlistSubmissionAttemptV1,
) {
  if (!haveSameImmutableAttempt(current, next)) return false;
  if (JSON.stringify(current.state) === JSON.stringify(next.state)) return true;
  if (current.state.kind === "pending-reconciliation") {
    return (
      next.state.kind === "accepted" || next.state.kind === "not-applied"
    );
  }
  return (
    current.state.kind === "not-applied" &&
    current.state.retryable &&
    current.state.reason === "mutation-not-invoked" &&
    next.state.kind === "pending-reconciliation"
  );
}

function createCapability(
  storage: Storage,
  attempt: AppointmentWaitlistSubmissionAttemptV1,
) {
  const capability: AppointmentWaitlistSubmissionAttemptReconciliationCapability =
    Object.freeze({
      kind: "appointment-waitlist-submission-attempt-reconciliation-capability",
    });
  capabilities.set(capability, {
    storage,
    draftId: attempt.draftId,
    customerAccountId: attempt.customerAccountId,
    idempotencyKey: attempt.idempotencyKey,
    immutableFingerprint: immutableAttemptFingerprint(attempt),
  });
  return capability;
}

/** Persist/read-back the pending row before the provider can be invoked. */
export function storeAppointmentWaitlistSubmissionPendingAttempt(
  value: unknown,
) {
  if (isCustomerOwnedSessionStorageQuarantined()) return null;
  const validation = validateAppointmentWaitlistSubmissionAttempt(value);
  const storage = getStorage();
  if (
    !validation.valid ||
    validation.attempt.state.kind !== "pending-reconciliation" ||
    !storage
  ) {
    return null;
  }
  const attempt = validation.attempt;
  const current = readEntries(storage);
  if (current === null) return null;
  const existing = current.find(({ draftId }) => draftId === attempt.draftId);
  if (
    existing &&
    (!haveSameImmutableAttempt(existing.attempt, attempt) ||
      !isLegalStateTransition(existing.attempt, attempt))
  ) {
    return null;
  }
  const otherEntries = current.filter(
    ({ draftId }) => draftId !== attempt.draftId,
  );
  const evictable = otherEntries
    .filter(({ attempt: candidate }) => candidate.state.kind === "not-applied")
    .sort((left, right) => left.savedAtEpochMs - right.savedAtEpochMs);
  while (otherEntries.length >= MAX_ENTRIES && evictable.length > 0) {
    const target = evictable.shift()!;
    const index = otherEntries.findIndex(
      ({ draftId }) => draftId === target.draftId,
    );
    if (index >= 0) otherEntries.splice(index, 1);
  }
  if (otherEntries.length >= MAX_ENTRIES && !existing) return null;
  const next = [
    { draftId: attempt.draftId, savedAtEpochMs: Date.now(), attempt },
    ...otherEntries,
  ];
  if (!persistEntries(storage, next)) return null;
  const confirmed = readEntries(storage)?.find(
    ({ draftId }) => draftId === attempt.draftId,
  )?.attempt;
  if (!confirmed || JSON.stringify(confirmed) !== JSON.stringify(attempt))
    return null;
  return {
    attempt: confirmed,
    reconciliationCapability: createCapability(storage, confirmed),
  };
}

/**
 * Exact CAS for the invocation that created the capability. This is the only
 * operation allowed to settle a pre-existing pending row during auth
 * quarantine; it cannot create, read, or rewrite another customer's row.
 */
export function settleAppointmentWaitlistSubmissionAttempt(input: {
  capability: AppointmentWaitlistSubmissionAttemptReconciliationCapability;
  attempt: unknown;
}) {
  const capability = capabilities.get(input.capability);
  const validation = validateAppointmentWaitlistSubmissionAttempt(input.attempt);
  if (!capability || !validation.valid) return null;
  const nextAttempt = validation.attempt;
  if (
    nextAttempt.draftId !== capability.draftId ||
    nextAttempt.customerAccountId !== capability.customerAccountId ||
    nextAttempt.idempotencyKey !== capability.idempotencyKey ||
    immutableAttemptFingerprint(nextAttempt) !== capability.immutableFingerprint ||
    nextAttempt.state.kind === "pending-reconciliation"
  ) {
    return null;
  }
  const entries = readEntries(capability.storage);
  const existing = entries?.find(
    ({ draftId }) => draftId === capability.draftId,
  );
  if (
    !entries ||
    !existing ||
    existing.attempt.state.kind !== "pending-reconciliation" ||
    immutableAttemptFingerprint(existing.attempt) !==
      capability.immutableFingerprint ||
    !isLegalStateTransition(existing.attempt, nextAttempt)
  ) {
    return null;
  }
  const nextEntries = [
    {
      ...existing,
      savedAtEpochMs: Date.now(),
      attempt: nextAttempt,
    },
    ...entries.filter(({ draftId }) => draftId !== capability.draftId),
  ];
  if (!persistEntries(capability.storage, nextEntries)) return null;
  const confirmed = readEntries(capability.storage)?.find(
    ({ draftId }) => draftId === capability.draftId,
  )?.attempt;
  return confirmed && JSON.stringify(confirmed) === JSON.stringify(nextAttempt)
    ? confirmed
    : null;
}

export function storeAppointmentWaitlistSubmissionAttempt(value: unknown) {
  if (isCustomerOwnedSessionStorageQuarantined()) return null;
  const validation = validateAppointmentWaitlistSubmissionAttempt(value);
  const storage = getStorage();
  if (!validation.valid || !storage) return null;
  const attempt = validation.attempt;
  const entries = readEntries(storage);
  const existing = entries?.find(({ draftId }) => draftId === attempt.draftId);
  if (
    !entries ||
    !existing ||
    !isLegalStateTransition(existing.attempt, attempt)
  ) {
    return null;
  }
  const nextEntries = [
    { ...existing, savedAtEpochMs: Date.now(), attempt },
    ...entries.filter(({ draftId }) => draftId !== attempt.draftId),
  ];
  return persistEntries(storage, nextEntries) ? attempt : null;
}

export function readAppointmentWaitlistSubmissionAttempt(
  draftId: string,
  customerAccountId: string,
) {
  if (isCustomerOwnedSessionStorageQuarantined()) return null;
  const storage = getStorage();
  const attempt = storage
    ? readEntries(storage)?.find(({ draftId: id }) => id === draftId)?.attempt
    : null;
  return attempt?.customerAccountId === customerAccountId ? attempt : null;
}

export function inspectAppointmentWaitlistSubmissionAttemptsPersisted(
  evaluatedAtEpochMs?: number,
) {
  if (isCustomerOwnedSessionStorageQuarantined()) {
    return { status: "unreadable", attempts: [] as const } as const;
  }
  const storage = getStorage(evaluatedAtEpochMs);
  if (!storage) return { status: "unreadable", attempts: [] as const } as const;
  try {
    const entries = readEntries(storage, evaluatedAtEpochMs);
    if (!entries) {
      return { status: "unreadable", attempts: [] as const } as const;
    }
    if (entries.length === 0) {
      return { status: "absent", attempts: [] as const } as const;
    }
    return {
      status: "present",
      attempts: Object.freeze(entries.map(({ attempt }) => attempt)),
    } as const;
  } catch {
    return { status: "unreadable", attempts: [] as const } as const;
  }
}

export function inspectAppointmentWaitlistSubmissionAttemptPersisted(
  draftId: string,
  evaluatedAtEpochMs?: number,
) {
  const inspected = inspectAppointmentWaitlistSubmissionAttemptsPersisted(
    evaluatedAtEpochMs,
  );
  if (inspected.status === "unreadable") {
    return { status: "unreadable" } as const;
  }
  const matches = inspected.attempts.filter(
    (attempt) => attempt.draftId === draftId,
  );
  return matches.length === 0
    ? ({ status: "absent" } as const)
    : matches.length === 1
      ? ({ status: "present", draft: matches[0] } as const)
      : ({ status: "unreadable" } as const);
}

/** Exact no-eviction create used only to restore a proven lifecycle snapshot. */
export function createAppointmentWaitlistSubmissionAttemptStrictPersisted(
  value: unknown,
  evaluatedAtEpochMs?: number,
) {
  const evaluatedEpoch = safeSubmissionAttemptEpoch(evaluatedAtEpochMs);
  if (evaluatedEpoch === null) return { status: "invalid" } as const;
  if (isCustomerOwnedSessionStorageQuarantined()) {
    return { status: "unreadable" } as const;
  }
  const validation = validateAppointmentWaitlistSubmissionAttempt(value, {
    evaluatedAtEpochMs: evaluatedEpoch,
  });
  const storage = getStorage(evaluatedEpoch);
  if (!validation.valid) return { status: "invalid" } as const;
  if (!storage) return { status: "unreadable" } as const;
  const attempt = validation.attempt;
  const entries = readEntries(storage, evaluatedEpoch);
  if (!entries) return { status: "unreadable" } as const;
  if (entries.some(({ draftId }) => draftId === attempt.draftId)) {
    return { status: "conflict", reason: "already-exists" } as const;
  }
  if (entries.length >= MAX_ENTRIES) return { status: "capacity" } as const;
  const next = [
    { draftId: attempt.draftId, savedAtEpochMs: evaluatedEpoch, attempt },
    ...entries,
  ];
  const persisted = persistEntries(storage, next, evaluatedEpoch);
  const confirmed = readEntries(storage, evaluatedEpoch);
  if (!confirmed) return { status: "unreadable" } as const;
  const readBack = confirmed.find(
    ({ draftId }) => draftId === attempt.draftId,
  )?.attempt;
  if (readBack && JSON.stringify(readBack) === JSON.stringify(attempt)) {
    return { status: "created", draft: readBack } as const;
  }
  return persisted
    ? ({ status: "readback-mismatch" } as const)
    : ({ status: "persistence-failed" } as const);
}

/** Exact compare-and-remove; a different same-id attempt is never retired. */
export function removeAppointmentWaitlistSubmissionAttemptStrictPersisted(
  value: unknown,
  evaluatedAtEpochMs?: number,
) {
  const evaluatedEpoch = safeSubmissionAttemptEpoch(evaluatedAtEpochMs);
  if (evaluatedEpoch === null) return { status: "invalid" } as const;
  if (isCustomerOwnedSessionStorageQuarantined()) {
    return { status: "unreadable" } as const;
  }
  const validation = validateAppointmentWaitlistSubmissionAttempt(value, {
    evaluatedAtEpochMs: evaluatedEpoch,
  });
  const storage = getStorage(evaluatedEpoch);
  if (!validation.valid) return { status: "invalid" } as const;
  if (!storage) return { status: "unreadable" } as const;
  const attempt = validation.attempt;
  const entries = readEntries(storage, evaluatedEpoch);
  if (!entries) return { status: "unreadable" } as const;
  const existing = entries.find(
    ({ draftId }) => draftId === attempt.draftId,
  );
  if (!existing) return { status: "absent" } as const;
  if (JSON.stringify(existing.attempt) !== JSON.stringify(attempt)) {
    return { status: "conflict", reason: "persisted-value-changed" } as const;
  }
  const next = entries.filter(
    ({ draftId }) => draftId !== attempt.draftId,
  );
  const persisted = persistEntries(storage, next, evaluatedEpoch);
  const confirmed = readEntries(storage, evaluatedEpoch);
  if (!confirmed) return { status: "unreadable" } as const;
  if (
    !confirmed.some(({ draftId }) => draftId === attempt.draftId) &&
    JSON.stringify(confirmed) === JSON.stringify(next)
  ) {
    return { status: "removed", draft: attempt } as const;
  }
  return persisted
    ? ({ status: "readback-mismatch" } as const)
    : ({ status: "persistence-failed" } as const);
}

export function ensureAppointmentWaitlistSubmissionAttemptRemoved(
  draftId: string,
  customerAccountId: string,
) {
  if (isCustomerOwnedSessionStorageQuarantined()) return false;
  const storage = getStorage();
  const entries = storage ? readEntries(storage) : null;
  if (!storage || !entries) return false;
  const existing = entries.find(({ draftId: id }) => id === draftId);
  if (!existing) return true;
  if (existing.attempt.customerAccountId !== customerAccountId) return false;
  return persistEntries(
    storage,
    entries.filter(({ draftId: id }) => id !== draftId),
  );
}

/** Retained owner-bound rows are intentionally excluded from auth sweeping. */
export function verifyAppointmentWaitlistSubmissionAttemptsOwnerBound() {
  const storage = getStorage();
  if (!storage) return false;
  const entries = readEntries(storage);
  return entries !== null;
}
